import React, { useEffect, useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  getMessageMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'

export default function usePrescriptionDataForTemplate({
  templateData,
  useEmptyBox = false,
  isSupportPortal = false,
  practitionerId,
  practitionerSlug,
}) {
  const [foundPrescriptionData, setFoundPrescriptionData] = useState(null)
  const [loading, setLoading] = useState(false)
  // Holds a clear, actionable error when the preview couldn't be initialized
  // (e.g. no template exists anywhere in the clinic brand to inherit from).
  const [loadError, setLoadError] = useState(null)

  const fetchDummyData = useCallback(async ({ forceReinitialize = false } = {}) => {
    setLoading(true)
    setLoadError(null)
    try {
      let response
      if (isSupportPortal && (practitionerId || practitionerSlug)) {
        const params = {
          clinicId: templateData?.clinicId,
        }
        // The support portal resolves the practitioner by slug or id; send whichever we
        // have (slug is the reliable identifier across the support endpoints).
        if (practitionerSlug) params['practitioner-slug'] = practitionerSlug
        if (practitionerId) params['practitioner-id'] = practitionerId
        if (templateData?.id) params.templatePrescriptionId = templateData.id
        if (forceReinitialize) params.forceReinitialize = true
        response = await getSPDataWithParams(
          '/api/secure/cc/support/practitioner/prescription/dummy',
          params,
        )
      } else {
        const params = {
          clinicId: templateData?.clinicId,
          empty: useEmptyBox,
        }
        if (forceReinitialize) params.forceReinitialize = true
        response = await getMPDataWithParams('/api/secure/doctor/prescription/dummy', params)
      }
      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const responseData = getDataMapFromCommonResponse(response)
        setFoundPrescriptionData(responseData?.metaPrescription)
        setLoadError(null)
        // Backend signals when this template was just auto-initialized for this
        // clinic (inherited from the clinic brand, or a starter sample created from
        // scratch) — make that visually clear to the doctor.
        if (responseData?.dummyBootstrappedFromBrandFallback) {
          toast.info(
            'A starter prescription template was set up for this clinic. Customize it as you like.',
            { toastId: 'rx-template-brand-bootstrap' },
          )
        }
      } else {
        // The success branch didn't match — surface the HTTP status so an auth/permission/
        // not-deployed failure (which carries no CommonResponse `message`) is diagnosable
        // instead of collapsing to a bland "could not load".
        setFoundPrescriptionData(null)
        const status = response?.status
        setLoadError({
          code: response?.data?.code,
          message:
            getMessageMapFromCommonResponse(response) ||
            (status
              ? `Could not load the preview (HTTP ${status}).`
              : 'Could not load the prescription template preview.'),
        })
      }
    } catch (err) {
      setFoundPrescriptionData(null)
      const status = err?.response?.status
      const rawBody =
        typeof err?.response?.data === 'string' && err.response.data.trim()
          ? err.response.data.trim().slice(0, 200)
          : null
      setLoadError({
        code: err?.response?.data?.code,
        message:
          err?.response?.data?.message ||
          (status
            ? `Could not load the preview (HTTP ${status})${rawBody ? `: ${rawBody}` : ''}.`
            : err?.message || 'Could not load the prescription template preview.'),
      })
    } finally {
      setLoading(false)
    }
  }, [
    templateData?.clinicId,
    templateData?.id,
    useEmptyBox,
    isSupportPortal,
    practitionerId,
    practitionerSlug,
  ])

  useEffect(() => {
    const shouldFetch = isSupportPortal
      ? templateData?.clinicId && (practitionerId || practitionerSlug)
      : templateData?.clinicId && templateData?.id
    if (shouldFetch) {
      setFoundPrescriptionData(null)
      fetchDummyData()
    }
  }, [
    templateData?.clinicId,
    templateData?.id,
    useEmptyBox,
    isSupportPortal,
    practitionerId,
    practitionerSlug,
    fetchDummyData,
  ])

  const finalData = {
    ...templateData,
    prescriptionData: foundPrescriptionData?.prescriptionData,
    patientPrefill: foundPrescriptionData?.patientPrefill,
  }
  return {
    loadingPrescriptionData: loading,
    currentTemplateWithDummyData: finalData,
    prescriptionDataLoadError: loadError,
    refetchPrescriptionData: fetchDummyData,
    // Rebuild the sample/dummy data for this clinic from scratch (forceReinitialize).
    regenerateSampleData: () => fetchDummyData({ forceReinitialize: true }),
  }
}
